/*
  MTH hero slideshow controller.

  A small, self-contained carousel for the homepage hero. It deliberately does
  NOT use Dawn's <slideshow-component>, because Brandon wants behaviour Dawn's
  slider can't do cleanly:
    - dots only (no arrows, no play/pause),
    - a progress ring around the active dot whose completion IS the advance
      cue — the slide moves exactly when the ring finishes its loop, and
    - a FORWARD-ONLY auto-loop — reaching the last slide it keeps moving RIGHT
      and wraps seamlessly, never auto-scrolling back left.

  Design that keeps it bug-free:
    * The ring drives autoplay. Each active dot's ring animates for the slide
      duration; its `animationend` fires the advance. So the ring and the
      timing are the SAME clock — they can never drift out of sync. (No
      setInterval running alongside a separate CSS animation.)
    * `this.index` (the logical current slide) is the single source of truth for
      the active dot. It is set explicitly on every advance / dot-click / swipe-
      settle. The scroll listener only restarts the ring when the settled slide
      differs from `this.index`, so a programmatic advance's own settle is a
      no-op and the active dot can't flicker backwards during a transition.
    * Forward-only wrap: a clone of slide 1 is appended. Autoplay glides RIGHT
      into the clone (identical to slide 1), then once motion settles the scroll
      position is reset to 0 instantly — invisible, because clone === slide 1.
    * Native swipe stays free (CSS scroll-snap on .slider--everywhere). Manual
      swipes and dot-clicks may go either direction (that's the visitor's
      choice); only the AUTOMATIC rotation is forced forward.
*/
if (!customElements.get('mth-hero-slideshow')) {
  class MthHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slider');
      if (!this.slider) return;

      this.slides = Array.from(this.slider.querySelectorAll('.slideshow__slide'));
      this.count = this.slides.length;
      this.dots = Array.from(this.querySelectorAll('.mth-dot'));
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayOn = this.dataset.autoplay === 'true' && this.count > 1 && !this.reduce;

      this.index = 0; // logical current slide (0..count-1)
      this.physical = 0; // physical scroll page (0..count; count === the clone)
      this.paused = false;

      // Append a clone of slide 1 so the auto-loop can move forward at the wrap.
      if (this.count > 1) {
        this.clone = this.slides[0].cloneNode(true);
        this.clone.classList.add('mth-slide-clone');
        this.clone.setAttribute('aria-hidden', 'true');
        this.clone
          .querySelectorAll('a, button, input, select, textarea, [tabindex]')
          .forEach((el) => el.setAttribute('tabindex', '-1'));
        // Don't let a cloned background <video> autoplay a second decode off-screen.
        this.clone.querySelectorAll('video').forEach((v) => {
          v.removeAttribute('autoplay');
          try {
            v.pause();
          } catch (e) {
            /* no-op */
          }
        });
        this.slider.appendChild(this.clone);
      }

      if (this.autoplayOn) this.classList.add('mth-slideshow--autoplay');
      // While auto-rotating, don't let every advance be announced by screen
      // readers (Dawn's SlideshowComponent muted the live region the same way).
      this.slider.setAttribute('aria-live', this.autoplayOn ? 'off' : 'polite');

      // Bound handlers (so disconnectedCallback can remove them symmetrically).
      this.onScroll = this.onScroll.bind(this);
      this.onAnimEnd = this.onAnimEnd.bind(this);
      this.onResize = this.onResize.bind(this);
      this.pause = () => this.setPaused(true);
      this.resume = () => this.setPaused(false);

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('animationend', this.onAnimEnd);
      window.addEventListener('resize', this.onResize);

      // Pause autoplay while the visitor is interacting with the hero — hover
      // (desktop), touch/drag (mobile: pointerdown fires where mouseenter never
      // does, so a slide can't auto-advance under the finger), or keyboard focus.
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('pointerdown', this.pause);
      this.addEventListener('pointerup', this.resume);
      this.addEventListener('pointercancel', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);

      // Theme editor: selecting a slide block should show + pause that slide
      // (this replaces Dawn's theme-editor.js, which assumed a <slideshow-
      // component> ancestor and threw on our custom element).
      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => {
          const i = this.slides.indexOf(event.target);
          if (i < 0) return;
          this.setPaused(true);
          this.goTo(i);
        };
        this.onBlockDeselect = (event) => {
          if (this.slides.indexOf(event.target) < 0) return;
          this.setPaused(false);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.setActive(0); // shows + starts the first dot's ring (if autoplay)
    }

    disconnectedCallback() {
      if (this.slider) this.slider.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('animationend', this.onAnimEnd);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerdown', this.pause);
      this.removeEventListener('pointerup', this.resume);
      this.removeEventListener('pointercancel', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.resume);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      clearTimeout(this.settleTimer);
      clearTimeout(this.resizeTimer);
    }

    slideWidth() {
      return this.slider.clientWidth || 1;
    }

    // The ring finished → advance forward. This is the ONLY autoplay trigger.
    onAnimEnd(event) {
      if (event.animationName !== 'mth-dot-ring-fill') return;
      if (!this.autoplayOn || this.paused) return;
      // Only the active dot's ring animates, but guard anyway.
      const activeRing = this.querySelector('.mth-dot.is-active .mth-dot__ring-progress');
      if (event.target !== activeRing) return;
      this.advance();
    }

    // Forward-only automatic step.
    advance() {
      // Safety: if we're still parked on the clone (the settle reset hasn't run
      // yet), snap to the real first slide instantly FIRST, so `physical` can
      // never march past the clone into empty space.
      if (this.physical >= this.count) {
        this.scrollToPhysical(0, false);
        this.physical = 0;
      }
      const nextPhysical = this.physical + 1; // 0→1→…→count-1→count(clone)
      this.physical = nextPhysical;
      this.index = nextPhysical % this.count;
      this.scrollToPhysical(nextPhysical, true);
      this.setActive(this.index); // set dot + (re)start its ring for the new dwell
      // The clone→slide-1 reset is done by the settle handler.
    }

    // Direct navigation from a dot click / editor block select — may go either
    // way (explicit intent).
    goTo(i) {
      if (i < 0 || i >= this.count) return;
      this.physical = i;
      this.index = i;
      this.scrollToPhysical(i, true);
      this.setActive(i);
    }

    scrollToPhysical(physical, smooth) {
      const left = physical * this.slideWidth();
      if (smooth) {
        this.slider.scrollTo({ left, behavior: 'smooth' });
        return;
      }
      // Force a TRULY instant jump. `behavior: 'auto'` is meant to be instant,
      // but the track has CSS `scroll-behavior: smooth` (for the swipe glide),
      // and Safari lets that CSS win — which would animate the clone→slide-1
      // reset LEFTWARD across every slide (the "bounce back"). Temporarily
      // pinning `scroll-behavior: auto` guarantees the reset is a hard cut, and
      // because the clone is pixel-identical to slide 1 the cut is invisible.
      const prev = this.slider.style.scrollBehavior;
      this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = left;
      this.slider.style.scrollBehavior = prev;
    }

    onScroll() {
      // Debounced settle — fires once the slider stops moving (auto OR user).
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 140);
    }

    settle() {
      const w = this.slideWidth();
      const p = Math.round(this.slider.scrollLeft / w);

      if (this.clone && p >= this.count) {
        // On (or past) the clone. Only snap back to the real first slide once
        // the glide has ACTUALLY arrived — a settle that fires mid-glide toward
        // the clone must not reset early (that would yank the scroll and bounce).
        const arrived = Math.abs(this.slider.scrollLeft - this.count * w) < Math.max(4, w * 0.03);
        if (arrived) {
          this.scrollToPhysical(0, false); // instant, invisible (clone === slide 1)
          this.physical = 0;
          if (this.index !== 0) {
            this.index = 0;
            this.setActive(0);
          }
        }
        return; // never treat the clone itself as a user-landed slide
      }

      // Real slide. Restart the ring/dwell ONLY if it actually changed — i.e. the
      // visitor swiped/dragged here. A programmatic advance already set this.index
      // to the target, so its own settle is a no-op and the ring keeps running
      // uninterrupted (stays synced to the duration).
      this.physical = p;
      if (p !== this.index) {
        this.index = p;
        this.setActive(p);
      }
    }

    setActive(i) {
      this.dots.forEach((dot, di) => {
        const on = di === i;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.updateSlideVisibility(i);
      this.restartRing();
    }

    // Keep only the current slide reachable — hide the others from AT and pull
    // their focusable content out of the tab order, so keyboard users can't tab
    // into an off-screen slide (which would scroll the track sideways).
    updateSlideVisibility(i) {
      this.slides.forEach((slide, si) => {
        const on = si === i;
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          if (on) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
    }

    // Force the active dot's ring animation to restart from empty.
    restartRing() {
      const ring = this.querySelector('.mth-dot.is-active .mth-dot__ring-progress');
      if (!ring) return;
      ring.style.animation = 'none';
      void ring.getBoundingClientRect(); // reflow so the animation re-triggers
      ring.style.animation = '';
    }

    setPaused(paused) {
      if (this.paused === paused) return;
      this.paused = paused;
      // CSS freezes the ring (animation-play-state) so animationend won't fire
      // while paused; on resume it finishes the REMAINING time, then advances.
      this.classList.toggle('is-paused', paused && this.autoplayOn);
    }

    onResize() {
      // Slide width changed (rotate / resize) → re-align to the current slide
      // instantly so the scroll position doesn't land between slides.
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.physical = this.physical % this.count;
        this.scrollToPhysical(this.physical, false);
      }, 150);
    }
  }

  customElements.define('mth-hero-slideshow', MthHeroSlideshow);
}
